import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import { Copy, Check, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

export default function QRDialog({ template, onClose }) {
  const [copied, setCopied] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  const open = !!template;
  const kioskUrl = template ? `${window.location.origin}/kiosk/${template.public_token}` : "";

  useEffect(() => {
    if (!template) return;
    (async () => {
      try {
        const res = await fetch(`${API}/templates/${template.template_id}/qr.png`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("qr failed");
        const blob = await res.blob();
        setImgUrl(URL.createObjectURL(blob));
      } catch (err) {
        log.error("QR load failed", err);
        toast.error("Impossible de charger le QR code");
      }
    })();
    return () => { if (imgUrl) URL.revokeObjectURL(imgUrl); setImgUrl(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const copyUrl = async () => {
    await navigator.clipboard.writeText(kioskUrl);
    setCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setCopied(false), 1500);
  };

  const printQR = () => {
    if (!imgUrl) return;
    const w = window.open("", "_blank", "width=700,height=800");
    if (!w) return;
    const doc = w.document;
    doc.title = `QR - ${template.title}`;
    const style = doc.createElement("style");
    style.textContent = `
      body { font-family: system-ui, sans-serif; text-align: center; padding: 60px 20px; color:#1E3A5F; }
      h1 { font-family: 'Manrope', system-ui, sans-serif; font-weight: 700; letter-spacing: -0.02em; font-size: 32px; margin-bottom: 8px; }
      p { color: #5F758F; margin-top: 24px; }
      .card { border: 1px solid #E1E9F0; border-radius: 16px; padding: 40px; max-width: 480px; margin: 0 auto; background:#F4F7FB; }
      img { width: 320px; height: 320px; }
    `;
    doc.head.appendChild(style);

    const card = doc.createElement("div");
    card.className = "card";
    const h1 = doc.createElement("h1");
    h1.textContent = template.title;
    const sub = doc.createElement("p");
    sub.style.margin = "8px 0 32px 0";
    sub.textContent = "Scannez ce QR code pour répondre";
    const img = doc.createElement("img");
    img.src = imgUrl;
    img.alt = "QR";
    const footer = doc.createElement("p");
    footer.style.fontSize = "12px";
    footer.style.marginTop = "24px";
    footer.textContent = "Questiomed · Salle d'attente";
    card.append(h1, sub, img, footer);
    doc.body.appendChild(card);

    img.onload = () => w.print();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">QR Cabinet</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#5F758F]">
          Imprimez ce QR code et affichez-le en salle d'attente. Chaque scan crée une nouvelle session anonyme.
        </p>
        <div className="flex justify-center py-4 bg-[#F4F7FB] rounded-md border border-[#E1E9F0]">
          {imgUrl ? (
            <img src={imgUrl} alt="QR" className="w-64 h-64" data-testid="qr-image" />
          ) : (
            <div className="w-64 h-64 flex items-center justify-center text-[#5F758F]">Chargement…</div>
          )}
        </div>
        <div className="flex gap-2">
          <Input value={kioskUrl} readOnly className="bg-white border-[#E1E9F0] text-sm" data-testid="qr-url" />
          <Button className="rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white" onClick={copyUrl}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </Button>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-md" onClick={onClose}>Fermer</Button>
          <Button className="rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white" onClick={printQR} disabled={!imgUrl} data-testid="qr-print">
            <Printer size={16} className="mr-2" /> Imprimer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
